import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { Environment } from '../Config/constants'
import { ISaleEntity } from '../Pages/Financial/Details/financial-details.hook'
import { IAddressEntity } from '../Services/api/addresses/addresses.interface'
import { ICustomerEntity } from '../Services/api/customers/customers.interface'
import { IOrderEntity } from '../Services/api/orders/orders.interface'
import { IServiceOrderProduct } from '../Services/api/service-orders/service-order.interface'
import { FormatMoney } from './formatMoney'

type NsOrderJson = Record<string, any>

function parseNsOrder(order?: Pick<IOrderEntity, 'nsOrderStringified' | 'orderStringified'>): NsOrderJson | null {
  const raw = order?.nsOrderStringified || order?.orderStringified
  if (!raw || raw === '{}' || (raw.endsWith('...') && raw.length < 80)) return null

  try {
    const parsed = JSON.parse(raw) as NsOrderJson
    if (!parsed || typeof parsed !== 'object' || Object.keys(parsed).length === 0) {
      return null
    }
    return parsed
  } catch {
    return null
  }
}

function resolveLocalizedName(value: unknown): string {
  if (!value) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'object' && value !== null) {
    const record = value as Record<string, string>
    return record.pt || record['pt-BR'] || Object.values(record).find(v => typeof v === 'string') || ''
  }
  return String(value)
}

function resolveCustomerFromNs(nsOrder: NsOrderJson): Partial<ICustomerEntity> {
  const personType = nsOrder.billing_customer_type === 'business' ? 'PJ' : 'PF'

  return {
    id: '',
    name: nsOrder.contact_name || nsOrder.billing_name || nsOrder.billing_business_name || nsOrder.customer?.name || '',
    email: nsOrder.contact_email || nsOrder.customer?.email || '',
    phone: nsOrder.contact_phone || nsOrder.billing_phone || '',
    document: nsOrder.contact_identification || '',
    personType,
    stateRegistration: nsOrder.billing_state_registration || '',
    addresses: [],
  }
}

function resolveAddressFromNsShipping(nsOrder: NsOrderJson): Partial<IAddressEntity> {
  const shipping = nsOrder.shipping_address
  if (!shipping || typeof shipping !== 'object') return {}

  return {
    name: shipping.name || shipping.address || '',
    number: shipping.number || '',
    locality: shipping.locality || '',
    city: shipping.city || '',
    province: shipping.province || '',
    zipcode: shipping.zipcode || '',
  }
}

function resolveAddressFromNsBilling(nsOrder: NsOrderJson): Partial<IAddressEntity> {
  if (!nsOrder.billing_address && !nsOrder.billing_zipcode) return {}

  return {
    name: typeof nsOrder.billing_address === 'string' ? nsOrder.billing_address : nsOrder.billing_address?.name || '',
    number: nsOrder.billing_number || '',
    locality: nsOrder.billing_locality || '',
    city: nsOrder.billing_city || '',
    province: nsOrder.billing_province || '',
    zipcode: nsOrder.billing_zipcode || '',
  }
}

function hasAddressContent(address?: Partial<IAddressEntity> | null): boolean {
  if (!address) return false
  return Boolean(address.id || address.name || address.zipcode || address.city)
}

function mapHubProductToSaleRow(product: IServiceOrderProduct) {
  const qty = Number(product.totalQuantity) || Number((product as any).quantity) || 0
  const unitPrice = Number(product.unitaryPrice) || 0
  const total = Number(product.finalPrice) || 0

  return {
    id: product.hubProductId || product.id,
    orderProductId: product.id,
    productionBatches: product.productionBatches,
    productionSummary: product.productionSummary,
    name: product.product?.name || 'Produto não informado',
    quantity: qty,
    variant: 'Não informado',
    sku: 'Não informado',
    price_unit: FormatMoney(unitPrice),
    total: FormatMoney(total),
    image: product.product?.images?.[0]?.link || Environment.IMAGE_PLACEHOLDER,
    hcpCurrentFlow: product.hcpCurrentFlow ?? null,
    hcpCurrentFlowStatusId: product.hcpCurrentFlowStatusId ?? null,
    hcpCurrentFlowCode: product.hcpCurrentFlowCode ?? null,
    hcpCurrentFlowStatusSequence: product.hcpCurrentFlowStatusSequence ?? null,
    hcpCurrentFlowDisplayCode: product.hcpCurrentFlowDisplayCode ?? null,
    hcpCurrentFlowStatusName: product.hcpCurrentFlowStatusName ?? null,
    hcpCurrentFlowStartedAt: product.hcpCurrentFlowStartedAt ?? null,
    hcpCurrentFlowDeadline: product.hcpCurrentFlowDeadline ?? null,
    hcpFinished: product.hcpFinished ?? null,
    hcpPreServiceOrderId: product.hcpPreServiceOrderId ?? null,
    productOrderCode: product.productOrderCode ?? null,
  }
}

function mapNsProductToSaleRow(product: NsOrderJson, index: number) {
  const qty = Number(product.quantity) || 1
  const price = Number(String(product.price || product.promotional_price || '0').replace(',', '.')) || 0
  const image = product.image?.src || product.images?.[0]?.src || Environment.IMAGE_PLACEHOLDER

  return {
    id: String(product.id || product.product_id || index),
    name:
      resolveLocalizedName(product.name) ||
      product.sku ||
      (product.product_id ? `Produto #${product.product_id}` : 'Produto'),
    quantity: qty,
    variant: 'Não informado',
    sku: product.sku || 'Não informado',
    price_unit: FormatMoney(Math.round(price * 100)),
    total: FormatMoney(Math.round(price * qty * 100)),
    image,
  }
}

function resolveSaleProducts(order: IOrderEntity, nsOrder: NsOrderJson | null): ISaleEntity['products'] {
  const hubProducts = (order.products ?? []).filter(Boolean)

  if (hubProducts.length) {
    return hubProducts.map(mapHubProductToSaleRow)
  }

  const nsProducts = nsOrder && Array.isArray(nsOrder.products) ? nsOrder.products : []
  if (nsProducts.length) {
    return nsProducts.map(mapNsProductToSaleRow)
  }

  return []
}

export function buildSaleFromOrder(orderData: IOrderEntity): ISaleEntity {
  const nsOrder = parseNsOrder(orderData)

  const customerFromHub = orderData.customer
  const customerFromNs = nsOrder ? resolveCustomerFromNs(nsOrder) : null
  const customer: ICustomerEntity = {
    id: customerFromHub?.id || '',
    name: customerFromHub?.name || customerFromHub?.companyName || customerFromNs?.name || '',
    email: customerFromHub?.email || customerFromNs?.email || '',
    document: customerFromHub?.document || customerFromNs?.document || '',
    phone: customerFromHub?.phone || customerFromNs?.phone || '',
    personType: (customerFromHub?.personType || customerFromNs?.personType || 'PF') as ICustomerEntity['personType'],
    stateRegistration: customerFromHub?.stateRegistration || customerFromNs?.stateRegistration || '',
    addresses: customerFromHub?.addresses || [],
  }

  const shippingFromHub = orderData.shippingAddress
  const shippingFromNs = nsOrder ? resolveAddressFromNsShipping(nsOrder) : {}
  const shippingAddress: IAddressEntity = {
    id: shippingFromHub?.id || (hasAddressContent(shippingFromNs) ? 'ns-shipping' : ''),
    name: shippingFromHub?.name || shippingFromNs.name || '',
    number: shippingFromHub?.number || shippingFromNs.number || '',
    locality: shippingFromHub?.locality || shippingFromNs.locality || '',
    city: shippingFromHub?.city || shippingFromNs.city || '',
    province: shippingFromHub?.province || shippingFromNs.province || '',
    zipcode: shippingFromHub?.zipcode || shippingFromNs.zipcode || '',
  } as IAddressEntity

  const billingFromHub = orderData.billingAddress
  const billingFromNs = nsOrder ? resolveAddressFromNsBilling(nsOrder) : {}
  const billingAddress: IAddressEntity = {
    id: billingFromHub?.id || (hasAddressContent(billingFromNs) ? 'ns-billing' : ''),
    name: billingFromHub?.name || billingFromNs.name || '',
    number: billingFromHub?.number || billingFromNs.number || '',
    locality: billingFromHub?.locality || billingFromNs.locality || '',
    city: billingFromHub?.city || billingFromNs.city || '',
    province: billingFromHub?.province || billingFromNs.province || '',
    zipcode: billingFromHub?.zipcode || billingFromNs.zipcode || '',
  } as IAddressEntity

  return {
    ...orderData,
    id: orderData.id,
    createdAt: format(new Date(orderData.createdAt), 'dd MMM yyyy', { locale: ptBR }),
    customer,
    total: FormatMoney(Number(orderData.totalValue || 0)),
    billingAddress,
    shippingAddress,
    products: resolveSaleProducts(orderData, nsOrder),
  }
}

export function formatSaleAddress(address?: Partial<IAddressEntity> | null): string {
  if (!hasAddressContent(address)) return 'Não informado.'

  return `${address?.name || ''} ${address?.number || ''}, ${address?.locality || ''} - ${address?.city || ''} - ${address?.province || ''} | ${address?.zipcode || ''}`.trim()
}

export function resolvePaymentDetails(orderData?: IOrderEntity) {
  const orderPayment = (orderData as any)?.orderPayment
  const firstTransaction = orderPayment?.transactions?.[0]
  const nsOrder = parseNsOrder(orderData)

  return {
    gateway:
      (orderData as any)?.nsPaymentGatewayName ||
      (orderData as any)?.nsPaymentGateway ||
      orderPayment?.gatewayName ||
      nsOrder?.gateway_name,
    status: orderPayment?.status || nsOrder?.payment_status,
    modality: firstTransaction?.paymentType || firstTransaction?.modality || nsOrder?.payment_details?.method,
    omieBankAccountName: firstTransaction?.omieBankAccountName,
    paymentDate: firstTransaction?.paymentDate || nsOrder?.paid_at,
    finalValue: firstTransaction?.finalValue || orderData?.totalValue,
  }
}
